<script setup lang="ts">
import { ref, toRef } from 'vue';
import { useDialogPluginComponent } from 'quasar';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { cloneDeep } from 'lodash';

import LocalizedSelect from '../base/LocalizedSelect.vue';
import { useFormsStore } from '@/stores';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import type { Locales } from '@/services/types/api.types';
import { provideEditLocale } from '@/composables/data-display-utils';

const { t } = useI18n();
const formsStore = useFormsStore();

// PROPS
const props = defineProps<{
  title: string;
  filterElementLabel: string;
  options: any;
  relocatedElement: any;
  name: string;
  targetName: string;
  languageEnabled: Locales[];
}>();

// CONST
const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();
const editLocale = provideEditLocale(toRef(() => props.languageEnabled));

// REFS
const relocatedElementModel = ref(cloneDeep(props.relocatedElement));
const relocatedElementName = ref(props.name);

// FORM
const { handleSubmit } = useForm({
  validationSchema: yup.object({
    [relocatedElementName.value]: yup.string().required(),
  }),
});

// FUNCTIONS
const onSubmit = handleSubmit(async (payload) => {
  relocatedElementModel.value.row[props.targetName] = payload[relocatedElementName.value];
  await wrapSavingOperation(
    async () => await formsStore.saveForm(relocatedElementModel.value.row),
    () => onDialogOK(),
  );
});
</script>
<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide">
    <q-card class="q-dialog-plugin" style="width: 600px">
      <q-card-section class="row">
        <div class="text-h6">{{ props.title }}</div>
      </q-card-section>
      <q-separator />
      <q-card-section>
        <localized-select
          :name="relocatedElementName"
          :label="props.filterElementLabel"
          :editLocale="editLocale"
          :options="props.options"
          emit-value
          map-options
          outlined
        />
      </q-card-section>
      <q-separator />
      <q-card-actions align="right">
        <q-btn color="primary" flat :label="t('global.cancel')" @click="onDialogCancel" />
        <q-btn color="primary" :label="t('global.save')" @click="onSubmit" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
